import { useReactiveVar } from '@apollo/client';
import DensityLargeIcon from '@mui/icons-material/DensityLarge';
import DensityMediumIcon from '@mui/icons-material/DensityMedium';
import DensitySmallIcon from '@mui/icons-material/DensitySmall';
import IconButton from '@mui/material/IconButton';
import { useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import type React from 'react';
import { useEffect } from 'react';
import { COMFORTABLE, COMPACT, LOCAL_STORAGE_KEYS, SPACIOUS, VIEW_CONTEXT } from 'src/constants';
import { getPersistedDensity, isValidDensity, persistDensity } from 'src/helpers/densityPersistence';
import { akomiMediaCardGridVar, updateAkomiMediaCardGridVar } from 'src/store/akomiMediaCardGridVar';
import type { DensityType } from 'src/types/store.types';

type ViewContextType = (typeof VIEW_CONTEXT)[keyof typeof VIEW_CONTEXT];
type DensityTarget = 'density' | 'listViewDensity';

interface DensityToggleProps {
  context?: ViewContextType;
  target?: DensityTarget;
}

const DensityToggle: React.FC<DensityToggleProps> = ({ context = VIEW_CONTEXT.LIBRARY, target = 'density' }) => {
  const state = useReactiveVar(akomiMediaCardGridVar);
  const currentDensity = state[target];
  const theme = useTheme();
  const inactiveColor = theme.palette.action.disabled;

  // biome-ignore lint/correctness/useExhaustiveDependencies: initialization effect should only run once on mount
  useEffect(() => {
    if (target === 'listViewDensity') {
      // List view density uses its own localStorage key
      try {
        const stored = window.localStorage.getItem(LOCAL_STORAGE_KEYS.LISTVIEW_DENSITY);
        if (stored && isValidDensity(stored)) {
          updateAkomiMediaCardGridVar({ listViewDensity: stored });
        }
      } catch {
        // localStorage not available
      }
    } else {
      const initialDensity = getPersistedDensity(context);
      updateAkomiMediaCardGridVar({ density: initialDensity });
    }
  }, []);

  const setDensity = (nextDensity: DensityType): void => {
    if (target === 'listViewDensity') {
      updateAkomiMediaCardGridVar({ listViewDensity: nextDensity });
      try {
        window.localStorage.setItem(LOCAL_STORAGE_KEYS.LISTVIEW_DENSITY, nextDensity);
      } catch {
        // localStorage not available
      }
    } else {
      updateAkomiMediaCardGridVar({ density: nextDensity });
      persistDensity(context, nextDensity);
    }
  };

  return (
    <div style={{ display: 'flex' }}>
      <Tooltip title="Compact" arrow enterDelay={50} placement="bottom">
        <IconButton
          onClick={() => setDensity(COMPACT)}
          size="large"
          aria-label="Compact density"
          data-testid="density-compact"
        >
          <DensitySmallIcon style={{ color: currentDensity === COMPACT ? 'inherit' : inactiveColor, fontSize: 20 }} />
        </IconButton>
      </Tooltip>
      <Tooltip title="Comfortable" arrow enterDelay={50} placement="bottom">
        <IconButton
          onClick={() => setDensity(COMFORTABLE)}
          size="large"
          aria-label="Comfortable density"
          data-testid="density-comfortable"
        >
          <DensityMediumIcon
            style={{ color: currentDensity === COMFORTABLE ? 'inherit' : inactiveColor, fontSize: 20 }}
          />
        </IconButton>
      </Tooltip>
      <Tooltip title="Spacious" arrow enterDelay={50} placement="bottom">
        <IconButton
          onClick={() => setDensity(SPACIOUS)}
          size="large"
          aria-label="Spacious density"
          data-testid="density-spacious"
        >
          <DensityLargeIcon style={{ color: currentDensity === SPACIOUS ? 'inherit' : inactiveColor, fontSize: 20 }} />
        </IconButton>
      </Tooltip>
    </div>
  );
};

export default DensityToggle;
